import { ref, computed } from '@vue/composition-api'
import { findIndex } from 'lodash'
import CustomerApi from '@/api/waiter/Customer'
import { handleApiError } from '@/utils/errorHandler'

// カテゴリ定数
export const NOTE_CATEGORIES = [
  { value: 'preference', text: '好み', color: 'primary' },
  { value: 'caution', text: '注意事項', color: 'warning' },
  { value: 'conversation', text: '会話メモ', color: 'success' },
  { value: 'other', text: 'その他', color: 'secondary' },
]

export default function useCustomerNotes(vm) {
  const notes = ref([])
  const loading = ref(false)
  const pagination = ref({
    page: 1,
    perPage: 10,
    totalCount: 0,
    totalPages: 0,
  })
  const selectedCategory = ref(null)
  const selectedAuthorId = ref(null)
  const editingNote = ref(null)
  const noteFormDialog = ref(false)

  // カテゴリラベルを取得
  const getCategoryLabel = categoryValue => {
    const category = NOTE_CATEGORIES.find(c => c.value === categoryValue)

    return category ? category.text : categoryValue
  }

  // カテゴリカラーを取得
  const getCategoryColor = categoryValue => {
    const category = NOTE_CATEGORIES.find(c => c.value === categoryValue)

    return category ? category.color : 'grey'
  }

  // メモ一覧を取得
  const fetchNotes = async (customerId, {
    page = 1, perPage = 10, category = null, authorId = null,
  } = {}) => {
    loading.value = true
    try {
      const res = await CustomerApi.getCustomerNotes(customerId, {
        page,
        perPage,
        category,
        authorId,
      })

      notes.value = res.data?.notes || []
      pagination.value = res.data?.pagination || {
        page: 1,
        perPage: 10,
        totalCount: 0,
        totalPages: 0,
      }

      return {
        notes: notes.value,
        pagination: pagination.value,
      }
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return {
        notes: [],
        pagination: {
          page: 1, perPage: 10, totalCount: 0, totalPages: 0,
        },
      }
    } finally {
      loading.value = false
    }
  }

  // メモを作成
  const createNote = async (customerId, { content, category = null, callback = null }) => {
    try {
      const res = await CustomerApi.createCustomerNote(customerId, {
        content,
        category,
      })

      if (res.data?.note) {
        // 新しいメモを先頭に追加
        notes.value = [res.data.note, ...notes.value]
        pagination.value.totalCount += 1
        vm.$toast.success('メモを作成しました')

        return res.data.note
      }

      return null
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return null
    } finally {
      if (callback) callback()
    }
  }

  // メモを更新
  const updateNote = async (customerId, noteId, { content, category = null, callback = null }) => {
    try {
      const res = await CustomerApi.updateCustomerNote(customerId, noteId, {
        content,
        category,
      })

      if (res.data?.note) {
        const updatedNote = res.data.note
        const noteIdx = findIndex(notes.value, note => note.id === updatedNote.id)
        if (noteIdx >= 0) {
          notes.value.splice(noteIdx, 1, updatedNote)
        }
        vm.$toast.success('メモを更新しました')

        return updatedNote
      }

      return null
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return null
    } finally {
      if (callback) callback()
    }
  }

  // メモを削除
  const deleteNote = async (customerId, noteId, { callback = null } = {}) => {
    try {
      await CustomerApi.deleteCustomerNote(customerId, noteId)

      const noteIdx = findIndex(notes.value, note => note.id === noteId)
      if (noteIdx >= 0) {
        notes.value.splice(noteIdx, 1)
        pagination.value.totalCount -= 1
      }
      vm.$toast.success('メモを削除しました')

      return true
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return false
    } finally {
      if (callback) callback()
    }
  }

  // 新規作成ダイアログを開く
  const openCreateDialog = () => {
    editingNote.value = null
    noteFormDialog.value = true
  }

  // 編集ダイアログを開く
  const openEditDialog = note => {
    editingNote.value = { ...note }
    noteFormDialog.value = true
  }

  // ダイアログを閉じる
  const closeDialog = () => {
    editingNote.value = null
    noteFormDialog.value = false
  }

  // ページ変更
  const changePage = async (customerId, page) => {
    await fetchNotes(customerId, {
      page,
      perPage: pagination.value.perPage,
      category: selectedCategory.value,
      authorId: selectedAuthorId.value,
    })
  }

  // カテゴリフィルター変更
  const changeCategory = async (customerId, category) => {
    selectedCategory.value = category
    await fetchNotes(customerId, {
      page: 1,
      perPage: pagination.value.perPage,
      category,
      authorId: selectedAuthorId.value,
    })
  }

  return {
    notes,
    loading,
    pagination,
    selectedCategory,
    selectedAuthorId,
    editingNote,
    noteFormDialog,
    NOTE_CATEGORIES,
    getCategoryLabel,
    getCategoryColor,
    fetchNotes,
    createNote,
    updateNote,
    deleteNote,
    openCreateDialog,
    openEditDialog,
    closeDialog,
    changePage,
    changeCategory,
  }
}
